'use client';

import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Clock, Filter, Download } from 'lucide-react';
import { MOCK_MORTALITY_RECORDS, MortalityRecord } from './mortalityData';

const STATUS_CONFIG = {
  confirmed: { label: 'Confirmed', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', bg: 'bg-gray-100', text: 'text-gray-600', icon: Clock },
  'under-investigation': { label: 'Investigating', bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle },
};

interface RowProps {
  record: MortalityRecord;
}

function MortalityRow({ record }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[record.status];
  const StatusIcon = cfg.icon;

  return (
    <>
      <tr
        className={`border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${record.thresholdBreached ? 'bg-red-50 hover:bg-red-100' : ''}`}
        onClick={() => setExpanded(e => !e)}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {record.thresholdBreached && <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />}
            <span className="text-xs font-mono text-gray-500">{record.recordNumber}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-gray-700">
          {new Date(record.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </td>
        <td className="px-4 py-3">
          <div>
            <p className="text-xs font-semibold text-gray-800">{record.flockNumber}</p>
            <p className="text-xs text-gray-400 truncate max-w-[140px]">{record.flockName}</p>
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-gray-600">{record.house}</td>
        <td className="px-4 py-3">
          <span className="text-sm font-bold text-red-600">{record.numberDead}</span>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm font-bold text-amber-600">{record.numberCulled}</span>
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs font-semibold ${record.dailyMortalityPct >= 0.5 ? 'text-red-600' : record.dailyMortalityPct >= 0.1 ? 'text-amber-600' : 'text-gray-600'}`}>
            {record.dailyMortalityPct.toFixed(3)}%
          </span>
        </td>
        <td className="px-4 py-3">
          <p className="text-xs text-gray-700 truncate max-w-[140px]">{record.suspectedCause}</p>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
            <StatusIcon size={10} />
            {cfg.label}
          </span>
        </td>
        <td className="px-4 py-3">
          {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50 border-b border-gray-100">
          <td colSpan={10} className="px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <p className="text-gray-400 font-medium mb-1">Population Change</p>
                <p className="text-gray-700">Opening: <strong>{record.openingCount.toLocaleString()}</strong></p>
                <p className="text-red-600">Deaths: <strong>−{record.numberDead}</strong></p>
                <p className="text-amber-600">Culled: <strong>−{record.numberCulled}</strong></p>
                <p className="text-gray-800 font-semibold">Closing: {record.closingCount.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-400 font-medium mb-1">Cause Details</p>
                <p className="text-gray-700">Suspected: <strong>{record.suspectedCause}</strong></p>
                <p className="text-gray-700">Confirmed: <strong>{record.confirmedCause ?? 'Pending lab'}</strong></p>
                <p className="text-gray-700">Category: <strong>{record.causeCategory}</strong></p>
              </div>
              <div>
                <p className="text-gray-400 font-medium mb-1">Disposal & Age</p>
                <p className="text-gray-700">Method: <strong>{record.disposalMethod}</strong></p>
                <p className="text-gray-700">Bird Age: <strong>{record.ageDays} days</strong></p>
                <p className="text-gray-700">Farm: <strong>{record.farm}</strong></p>
              </div>
              <div>
                <p className="text-gray-400 font-medium mb-1">Notes</p>
                <p className="text-gray-600 leading-relaxed">{record.notes}</p>
                <p className="text-gray-400 mt-1">Recorded by: {record.recordedBy}</p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function MortalityTable() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [farmFilter, setFarmFilter] = useState('all');
  const [sortField, setSortField] = useState<'date' | 'numberDead' | 'dailyMortalityPct'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    let data = [...MOCK_MORTALITY_RECORDS];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        r.flockNumber.toLowerCase().includes(q) ||
        r.flockName.toLowerCase().includes(q) ||
        r.house.toLowerCase().includes(q) ||
        r.suspectedCause.toLowerCase().includes(q) ||
        r.recordNumber.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') data = data.filter(r => r.status === statusFilter);
    if (farmFilter !== 'all') data = data.filter(r => r.farm === farmFilter);
    data.sort((a, b) => {
      let av: number | string = a[sortField];
      let bv: number | string = b[sortField];
      if (sortField === 'date') { av = a.date; bv = b.date; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [search, statusFilter, farmFilter, sortField, sortDir]);

  function toggleSort(field: typeof sortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }

  const SortIcon = ({ field }: { field: typeof sortField }) =>
    sortField === field
      ? (sortDir === 'desc' ? <ChevronDown size={12} className="text-green-600" /> : <ChevronUp size={12} className="text-green-600" />)
      : <ChevronDown size={12} className="text-gray-300" />;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search flock, house, cause..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
        >
          <option value="all">All Statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
          <option value="under-investigation">Investigating</option>
        </select>
        <select
          value={farmFilter}
          onChange={e => setFarmFilter(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
        >
          <option value="all">All Farms</option>
          <option value="Idudi Poultry Farm">Idudi Poultry Farm</option>
          <option value="Kampala Demo Farm">Kampala Demo Farm</option>
        </select>
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-500">
          <Filter size={13} />
          <span>{filtered.length} records</span>
          <button className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Download size={13} />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Record #</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 cursor-pointer" onClick={() => toggleSort('date')}>
                <span className="flex items-center gap-1">Date <SortIcon field="date" /></span>
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Flock</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">House</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 cursor-pointer" onClick={() => toggleSort('numberDead')}>
                <span className="flex items-center gap-1">Deaths <SortIcon field="numberDead" /></span>
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Culled</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 cursor-pointer" onClick={() => toggleSort('dailyMortalityPct')}>
                <span className="flex items-center gap-1">Daily % <SortIcon field="dailyMortalityPct" /></span>
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Suspected Cause</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500">Status</th>
              <th className="px-4 py-2.5 w-8" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-400">
                  No mortality records found matching your filters.
                </td>
              </tr>
            ) : (
              filtered.map(record => <MortalityRow key={record.id} record={record} />)
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination stub */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-500">
        <span>Showing {filtered.length} of {MOCK_MORTALITY_RECORDS.length} records</span>
        <div className="flex items-center gap-1">
          <button className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40" disabled>Previous</button>
          <button className="px-3 py-1.5 border border-gray-200 rounded-lg bg-green-600 text-white font-medium">1</button>
          <button className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50">Next</button>
        </div>
      </div>
    </div>
  );
}
